import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { getSiteSettings } from '@/lib/siteSettings';
import { toast } from 'sonner';
import { Mail, User, MessageSquare, Send } from 'lucide-react';
import SEO from '@/components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    getSiteSettings().then(r => setSettings(r.data)).catch(console.error);
  }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Thank you! Your message has been sent.');
      setFormData({ name: '', email: '', message: '' });
    } catch (err) { toast.error(err.response?.data?.detail && typeof err.response.data.detail === 'string' ? err.response.data.detail : 'Failed to send. Please try again.'); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24" data-testid="contact-page">
      <SEO title="Contact - Gangadhar Som" description="Get in touch with artist Gangadhar Som for commissions, inquiries, and collaborations."
        url={`${window.location.origin}/contact`} />
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="grid md:grid-cols-2 gap-10 sm:gap-16 md:gap-24">
          {/* Info */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} className="space-y-8 sm:space-y-12">
            <div>
              <span className="font-sans uppercase tracking-[0.2em] text-xs font-medium text-muted-foreground">Get in Touch</span>
              <h1 className="font-serif text-3xl sm:text-4xl md:text-6xl font-light tracking-tight mt-3 mb-5 sm:mb-8" data-testid="contact-page-title">
                {settings?.contact_title || 'Contact'}
              </h1>
              <p className="font-sans text-sm sm:text-base md:text-lg leading-relaxed text-foreground/80">
                {settings?.contact_description || 'For inquiries about artwork, commissions, or exhibitions, please reach out.'}
              </p>
            </div>
            <div className="space-y-6">
              <div>
                <h2 className="font-serif text-xl sm:text-2xl font-light mb-2">Location</h2>
                <p className="font-sans text-sm sm:text-base text-foreground/80">{settings?.contact_location || 'Bangalore & Mysore, Karnataka, India'}</p>
              </div>
              {settings?.contact_email && (
                <div>
                  <h2 className="font-serif text-xl sm:text-2xl font-light mb-2">Email</h2>
                  <a href={`mailto:${settings.contact_email}`} className="font-sans text-sm sm:text-base text-accent hover:underline">{settings.contact_email}</a>
                </div>
              )}
              {settings?.contact_phone && (
                <div>
                  <h2 className="font-serif text-xl sm:text-2xl font-light mb-2">Phone</h2>
                  <a href={`tel:${settings.contact_phone}`} className="font-sans text-sm sm:text-base text-accent hover:underline">{settings.contact_phone}</a>
                </div>
              )}
              {settings?.contact_notable_works && (
                <div>
                  <h2 className="font-serif text-xl sm:text-2xl font-light mb-2">Notable Works</h2>
                  <p className="font-sans text-sm sm:text-base text-foreground/80">{settings.contact_notable_works}</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Form */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-card rounded-lg p-5 sm:p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div>
                <label htmlFor="name" className="block font-sans uppercase tracking-[0.2em] text-[10px] sm:text-xs font-medium text-muted-foreground mb-1.5">Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required
                    data-testid="contact-name-input"
                    className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg font-sans text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                    placeholder="Your name" />
                </div>
              </div>
              <div>
                <label htmlFor="email" className="block font-sans uppercase tracking-[0.2em] text-[10px] sm:text-xs font-medium text-muted-foreground mb-1.5">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required
                    data-testid="contact-email-input"
                    className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg font-sans text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                    placeholder="your@email.com" />
                </div>
              </div>
              <div>
                <label htmlFor="message" className="block font-sans uppercase tracking-[0.2em] text-[10px] sm:text-xs font-medium text-muted-foreground mb-1.5">Message</label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 text-muted-foreground" size={16} />
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5}
                    data-testid="contact-message-input"
                    className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg font-sans text-sm focus:outline-none focus:ring-2 focus:ring-accent resize-none"
                    placeholder="Your message..." />
                </div>
              </div>
              <button type="submit" disabled={isSubmitting} data-testid="contact-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent text-white font-sans uppercase tracking-[0.2em] text-xs sm:text-sm font-medium rounded-full hover:bg-accent/90 transition-all btn-hover disabled:opacity-50 active:scale-[0.98]">
                {isSubmitting ? 'Sending...' : <><Send size={14} /> Send Message</>}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
